import { useParams } from "react-router-dom";
import BrowserHeader from "../../../components/BrowserHeader/BrowserHeader";
import {
	CardBody,
	CardContainer,
	CardHeader,
} from "../../../components/DashboardCard/DashboardCard";
import { useCallback, useEffect, useState } from "react";
import { getFieldTemplateById } from "./manageFieldTemplates";
import {
	CreditFieldTemplate,
	DateFieldTemplate,
	ListFieldTemplate,
	TextFieldTemplate,
	MediaFieldTemplate,
	PhysicalComponentFieldTemplate,
	LinkFieldTemplate,
} from "./ProgramInterfaces";

import CreditFieldForm from "./FieldTemplateForms/CreditFieldForm";
import DateFieldForm from "./FieldTemplateForms/DateFieldForm";
import ListFieldForm from "./FieldTemplateForms/ListFieldForm";
import TextFieldForm from "./FieldTemplateForms/TextFieldTemplate";
import PhysicalComponentFieldForm from "./FieldTemplateForms/PhysicalComponentFieldForm";
import LinkFieldForm from "./FieldTemplateForms/LinkFieldForm";
import MediaFieldForm from "./FieldTemplateForms/MediaFieldForm";

const EditFieldTemplate = () => {
	const { programId, fieldType, fieldId } = useParams<{
		programId: string;
		fieldType: FieldTemplateType;
		fieldId: string;
	}>();

	const [fieldTemplate, setFieldTemplate] = useState<
		| DateFieldTemplate
		| ListFieldTemplate
		| TextFieldTemplate
		| PhysicalComponentFieldTemplate
		| LinkFieldTemplate
		| CreditFieldTemplate
		| MediaFieldTemplate
		| null
	>(null);

	useEffect(() => {
		getFieldTemplateById(Number(programId), fieldType, Number(fieldId)).then(
			(response) => {
				setFieldTemplate(response.data);
			}
		);
	}, [programId, fieldType, fieldId]);

	const renderFieldTemplate = useCallback(() => {
		if (!fieldTemplate) return <></>;
		switch (fieldType) {
			case FieldTemplateType.Credit:
				return (
					<CreditFieldForm
						formData={fieldTemplate as CreditFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.Date:
				return (
					<DateFieldForm
						formData={fieldTemplate as DateFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.List:
				return (
					<ListFieldForm
						formData={fieldTemplate as ListFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.Text:
				return (
					<TextFieldForm
						formData={fieldTemplate as TextFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.PhysicalComponent:
				return (
					<PhysicalComponentFieldForm
						formData={fieldTemplate as PhysicalComponentFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.Link:
				return (
					<LinkFieldForm
						formData={fieldTemplate as LinkFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			case FieldTemplateType.Media:
				return (
					<MediaFieldForm
						formData={fieldTemplate as MediaFieldTemplate}
						hideModal={() => {}}
						fullPage
						programId={programId}
					/>
				);
			default:
				return <></>;
		}
	}, [fieldTemplate, fieldType, programId]);

	return (
		<CardContainer>
			<BrowserHeader title={`Edit Field Template`} />
			<CardHeader className="justify-between gap-[1rem] lg:!flex-nowrap">
				<div className="flex flex-col gap-[1rem]">
					<h2 className="card-title">Edit Field Template</h2>
					<span className="text-[1.375rem] text-colorCopyLightLight">
						{formatEnumText(fieldType)} Field
					</span>
				</div>
			</CardHeader>

			<CardBody>{renderFieldTemplate()}</CardBody>
		</CardContainer>
	);
};

export default EditFieldTemplate;

export enum FieldTemplateType {
	Date = "date",
	List = "list",
	Text = "text",
	PhysicalComponent = "physicalComponent",
	Link = "link",
	Credit = "credit",
	Media = "media",
}

function formatEnumText(enumValue: string): string {
	// Insert a space before all caps and capitalize the first letter
	return enumValue
		.replace(/([A-Z])/g, " $1") // Add space before capital letters
		.replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
		.trim();
}
